// ============================================================================
// Intake question set for the public partner application (/apply/:code).
//
// ⛔ THIS COPY IS FINAL AND IT IS NOT MINE TO REWORD. Every English and Spanish
// string below is taken verbatim from the reviewed subcontractor vetting form
// (September 2026), which went through a native-speaker Spanish review. The
// Spanish uses "usted" throughout and avoids region-specific slang so it reads
// the same in Kansas City, Houston, Denver and Austin. Changing a word here is
// a content decision, not a code change — take it back to the form.
// Exception, by Ben's decision: the intro, Q1 owner option, Q2, Q3, Q6, Q7 help and Q8 were
// reworded for the independent-contractor relationship on 2026-10-19 (item 871), English and
// Spanish supplied together with his approved wording.
//
// The engine (engine.ts + intake-page.tsx) renders whatever this schema
// describes and never hard-codes question text.
//
// Schema notes:
//  • Bilingual: every string carries { en, es }.
//  • Progressive: questions are grouped into TRANCHES shown one at a time.
//  • NO EARLY EXIT (TCSD-0493, Ben — ruled before 3 Sep, on 3 Sep and again
//    on 14 Oct 2026). Every applicant answers every question, however their
//    first answers go. ⛔ Never add a gate, a strike threshold or a short
//    thank-you that ends the form early.
//  • `strike: true` on an option marks an answer that COUNTS AGAINST the
//    applicant. The applicant never sees it; the account manager sees a red
//    "Counts against" tag on the sub's profile (TCSD-0482). It ends nothing.
//  • Signals: `signal` maps an answer onto the structured fit columns
//    (owner_operated, performs_directly, bench_depth, market) that drive AI fit-scoring. `signalTrue` lists the option values
//    that count as true for boolean signals.
//  • Contact fields: `contact` maps an answer into the prospect contact
//    columns (business_name, owner_name, phone, email).
//
// The medical-experience yes/no was retired on 2026-10-26 (item #536, option 2):
// no question ever fed it. Medical readiness is read from the operating-room
// checklist answer in Part two, which the scoring assistant gets in full.
//
// ⚠️ `market` is NOT on the vetting form and is kept anyway: the referral code
// carries only its kind, so without this answer a submitted application cannot
// be routed to a region and there is no other source for it.
//
// Design intent: multiple-choice heavy, "just complex enough that only serious
// people finish"; ideal partner = owner on site, performs the work directly (no
// re-sub), has bench depth, and runs an established, independent business.
// ============================================================================

export interface L10n {
  en: string;
  es: string;
}

export type SignalKey =
  | 'owner_operated'
  | 'performs_directly'
  | 'bench_depth'
  | 'market';

export type ContactKey = 'business_name' | 'owner_name' | 'phone' | 'email';

export interface IntakeOption {
  value: string;
  label: L10n;
  /** Counts against the applicant — tagged for the account manager on the profile, never shown to the applicant, ends nothing. */
  strike?: boolean;
}

export interface IntakeQuestion {
  id: string;
  /** `long_text` is `text` in a textarea — Part two answers are paragraphs. */
  type: 'choice' | 'number_choice' | 'text' | 'long_text';
  prompt: L10n;
  help?: L10n;
  options?: IntakeOption[];
  required?: boolean;
  /** Structured fit-signal this answer feeds. */
  signal?: SignalKey;
  /** For boolean signals: which option values mean TRUE. */
  signalTrue?: string[];
  /** For bench_depth: map option value → integer. */
  signalNumber?: Record<string, number>;
  /** Contact column this answer feeds. */
  contact?: ContactKey;
  inputMode?: 'text' | 'tel' | 'email';
}

export interface IntakeTranche {
  id: string;
  title: L10n;
  questions: IntakeQuestion[];
}


export const INTAKE_STRINGS: Record<'en' | 'es', Record<string, string>> = {
  en: {
    title: 'Partner with TCS',
    subtitle: 'Terminal Cleaning Services — healthcare facility cleaning',
    intro:
      'We work with owner-operated cleaning businesses on recurring medical-facility contracts with clear client scopes and room to grow. This takes about 5 minutes.',
    start: 'Get started',
    next: 'Continue',
    back: 'Back',
    submit: 'Submit application',
    submitting: 'Submitting…',
    required: 'Please answer to continue.',
    invalid_phone: 'That phone number is not complete. Please enter all 10 digits.',
    invalid_email: 'That email address does not look right. Please check it.',
    invalid_link: 'This link isn’t valid. Please ask your contact for a new one.',
    done_title: 'Thank you!',
    done_body:
      'Your application is in. A TCS account manager will reach out about next steps for your area.',
    error: 'Something went wrong submitting. Please try again.',
    lang_toggle: 'Español',
    step: 'Step',
    of: 'of',
  },
  es: {
    title: 'Asóciese con TCS',
    subtitle: 'Terminal Cleaning Services — limpieza de instalaciones médicas',
    intro:
      'Trabajamos con negocios de limpieza operados por sus dueños en contratos recurrentes de instalaciones médicas, con alcances claros definidos por el cliente y espacio para crecer. Esto toma unos 5 minutos.',
    start: 'Comenzar',
    next: 'Continuar',
    back: 'Atrás',
    submit: 'Enviar solicitud',
    submitting: 'Enviando…',
    required: 'Por favor responda para continuar.',
    invalid_phone: 'Ese numero de telefono esta incompleto. Por favor escriba los 10 digitos.',
    invalid_email: 'Ese correo electronico no parece correcto. Por favor revise.',
    invalid_link: 'Este enlace no es válido. Pida uno nuevo a su contacto.',
    done_title: '¡Gracias!',
    done_body:
      'Recibimos su solicitud. Un gerente de cuentas de TCS se comunicará con usted sobre los próximos pasos en su área.',
    error: 'Algo salió mal al enviar. Por favor intente de nuevo.',
    lang_toggle: 'English',
    step: 'Paso',
    of: 'de',
  },
};

export const INTAKE_TRANCHES: IntakeTranche[] = [
  {
    id: 't1_business',
    title: { en: 'Your business', es: 'Su negocio' },
    questions: [
      {
        id: 'owner_name',
        type: 'text',
        required: true,
        contact: 'owner_name',
        prompt: { en: 'Your name', es: 'Su nombre' },
      },
      {
        id: 'business_name',
        type: 'text',
        required: true,
        contact: 'business_name',
        prompt: { en: 'Business name', es: 'Nombre del negocio' },
      },
      {
        id: 'phone',
        type: 'text',
        required: true,
        contact: 'phone',
        inputMode: 'tel',
        prompt: { en: 'Phone', es: 'Teléfono' },
      },
      {
        id: 'email',
        type: 'text',
        required: false,
        contact: 'email',
        inputMode: 'email',
        prompt: { en: 'Email', es: 'Correo electrónico' },
      },
      {
        id: 'heard_about',
        type: 'text',
        required: false,
        prompt: {
          en: 'Where did you hear about us?',
          es: '¿Cómo se enteró de nosotros?',
        },
      },
      {
        // ⚠️ Not on the vetting form. Kept because the referral code carries only
        // its kind, so nothing else can route a submitted application to a region.
        //
        // ⛔ THE CITIES ARE NOT TYPED HERE (item 1052, Ben 2026-11-02, decision 1B).
        // They come from the master market list, delivered with the link check
        // (sourcing_intake_context → markets) and put in front of the options
        // below by engine.withMarketOptions. A typed list here showed six cities
        // while the company sold in fourteen. Add a market to `markets` and it is
        // on this form; never retype one here. Only the fixed tail lives in code.
        id: 'market',
        type: 'choice',
        required: true,
        signal: 'market',
        prompt: { en: 'Which area do you work in?', es: '¿En qué área trabaja?' },
        options: [
          { value: 'Other', label: { en: 'Somewhere else', es: 'Otro lugar' } },
        ],
      },
    ],
  },
  {
    id: 't2_how_you_work',
    title: {
      en: 'Part one — about how you work',
      es: 'Parte uno — sobre cómo trabaja',
    },
    questions: [
      {
        id: 'business_shape',
        type: 'choice',
        required: true,
        signal: 'owner_operated',
        signalTrue: ['owner_supervises'],
        prompt: {
          en: 'Which of these best describes your business today?',
          es: '¿Cuál de estas opciones describe mejor su negocio hoy?',
        },
        options: [
          {
            value: 'owner_supervises',
            label: {
              en: 'I am the owner and I personally supervise every night of service.',
              es: 'Soy el dueño y superviso personalmente cada noche de servicio.',
            },
          },
          {
            value: 'hires_others',
            label: {
              en: 'I run the business and hire other people to do the nightly cleaning.',
              es: 'Dirijo el negocio y contrato a otras personas para hacer la limpieza nocturna.',
            },
            strike: true,
          },
          {
            value: 'occasional',
            label: {
              en: 'I am looking for occasional work to fill gaps in my schedule.',
              es: 'Busco trabajo ocasional para llenar huecos en mi horario.',
            },
            strike: true,
          },
        ],
      },
      {
        id: 'shift_cover',
        type: 'number_choice',
        required: true,
        signal: 'bench_depth',
        // Bench depth is "trained people other than you who can cover tonight".
        // Covering it yourself is a good owner-operator answer and still a bench
        // of zero; rescheduling or skipping is the one a medical facility cannot
        // carry, so it is the strike.
        signalNumber: { backup_on_call: 1, cover_myself: 0, team_handles: 2, reschedule: 0 },
        prompt: {
          en: 'When you cannot cover a night of service yourself, what do you do?',
          es: 'Cuando usted no puede cubrir una noche de servicio, ¿qué hace?',
        },
        options: [
          {
            value: 'backup_on_call',
            label: {
              en: 'I have a trained backup person on call.',
              es: 'Tengo una persona de respaldo capacitada y disponible.',
            },
          },
          {
            value: 'cover_myself',
            label: { en: 'I go in and cover it myself.', es: 'Voy yo mismo y lo cubro.' },
          },
          {
            value: 'team_handles',
            label: { en: 'I let my team handle it.', es: 'Dejo que mi equipo se encargue.' },
          },
          {
            value: 'reschedule',
            label: {
              en: 'I call the client to reschedule or skip that night.',
              es: 'Llamo al cliente para reprogramar o saltar esa noche.',
            },
            strike: true,
          },
        ],
      },
      {
        // The id stays `main_income` so stored answers keep their key (item 871, Ben 2026-10-19).
        // Neither answer counts for or against; the AI fit scorer is told not to weigh it.
        // Answers given to the earlier wording ('main' / 'side') render through RETIRED_OPTION_LABELS in answers.ts.
        id: 'main_income',
        type: 'choice',
        required: true,
        prompt: {
          en: 'Does your business currently serve other clients?',
          es: '¿Su negocio atiende actualmente a otros clientes?',
        },
        options: [
          {
            value: 'serves_others',
            label: { en: 'Yes, we serve other clients.', es: 'Sí, atendemos a otros clientes.' },
          },
          {
            value: 'first_clients',
            label: {
              en: 'Not yet, this would be one of our first clients.',
              es: 'Todavía no, este sería uno de nuestros primeros clientes.',
            },
          },
        ],
      },
      {
        id: 'who_cleans',
        type: 'choice',
        required: true,
        signal: 'performs_directly',
        signalTrue: ['own_team'],
        prompt: {
          en: 'Who does the cleaning work you take on?',
          es: '¿Quién hace el trabajo de limpieza que usted acepta?',
        },
        options: [
          {
            value: 'own_team',
            label: { en: 'My own team does the work.', es: 'Mi propio equipo hace el trabajo.' },
          },
          {
            value: 'subcontract',
            label: {
              en: 'I hire other cleaning companies to fulfill my contracts.',
              es: 'Contrato a otras compañías de limpieza para cumplir mis contratos.',
            },
            strike: true,
          },
        ],
      },
      {
        // Jack's question, via Ben 2026-11-02 (item 1052). English is Jack's
        // wording verbatim. Spanish is a draft shipped by Ben's decision (7A) and
        // NOT YET native-speaker reviewed like the rest of this form. Feeds no
        // signal; the fit scorer reads it with every other answer. No option
        // counts against the applicant (Ben, 8A).
        id: 'terminal_experience',
        type: 'choice',
        required: true,
        prompt: {
          en: 'Do you have terminal cleaning experience?',
          es: '¿Tiene experiencia en limpieza terminal?',
        },
        options: [
          {
            value: 'recurring_contract',
            label: { en: 'Yes, on a recurring contract', es: 'Sí, con un contrato recurrente' },
          },
          {
            value: 'one_time',
            label: {
              en: 'Yes, but only one-time or occasional jobs',
              es: 'Sí, pero solo en trabajos únicos u ocasionales',
            },
          },
          {
            value: 'other_medical',
            label: {
              en: 'No, but we have other medical facility cleaning experience',
              es: 'No, pero tenemos experiencia limpiando otras instalaciones médicas',
            },
          },
          {
            value: 'none_yet',
            label: {
              en: 'No medical cleaning experience yet',
              es: 'Todavía no tenemos experiencia en limpieza médica',
            },
          },
        ],
      },
    ],
  },
  {
    id: 't3_tell_us_more',
    title: {
      en: 'Part two — tell us more',
      es: 'Parte dos — cuéntenos más',
    },
    questions: [
      {
        id: 'hard_feedback',
        type: 'long_text',
        required: true,
        prompt: {
          en: 'Tell us about a time a client gave you hard feedback on your work.',
          es: 'Cuéntenos de una vez que un cliente le dio una crítica difícil sobre su trabajo.',
        },
        help: {
          en: 'What was the problem, and what did you do about it?',
          es: '¿Cuál fue el problema y qué hizo usted al respecto?',
        },
      },
      {
        id: 'checklist_discipline',
        type: 'long_text',
        required: true,
        prompt: {
          en: 'Client facilities require strict checklists, including operating rooms.',
          es: 'Las instalaciones de los clientes exigen listas de verificación estrictas, incluyendo salas de operaciones.',
        },
        help: {
          en: 'How do you make sure your team follows a checklist every single night, instead of getting comfortable after a few weeks?',
          es: '¿Cómo se asegura de que su equipo siga la lista todas las noches, en lugar de confiarse después de unas semanas?',
        },
      },
      {
        id: 'growth_fit',
        type: 'long_text',
        required: true,
        prompt: {
          en: 'We build long-term partnerships, not temporary jobs.',
          es: 'Construimos sociedades a largo plazo, no trabajos temporales.',
        },
        help: {
          en: 'New buildings are offered as they open. Each offer is yours to accept or decline. How does adding buildings fit your growth plans?',
          es: 'Ofrecemos nuevos edificios a medida que se abren. Cada oferta es suya para aceptar o rechazar. ¿Cómo encaja agregar edificios en sus planes de crecimiento?',
        },
      },
      {
        id: 'photo_feedback',
        type: 'long_text',
        required: true,
        prompt: {
          en: 'How do you handle feedback from clients, including photos of deficiencies?',
          es: '¿Cómo responde a los comentarios de los clientes, incluyendo fotos de deficiencias?',
        },
        help: {
          en: 'Walk us through how you fix the problem and how you keep it from happening again.',
          es: 'Explíquenos cómo corrige el problema y cómo evita que vuelva a ocurrir.',
        },
      },
    ],
  },
];
